import { debounce } from 'lodash-es'

/**
 * Factory function for invoice calculations
 * Handles recalculation of invoice totals, discounts, and row numbering
 * @param {Object} context - Vue component context (this)
 * @returns {Object} Calculation methods
 */
export function useInvoiceCalculations (context) {
  /**
   * Recalculate row numbers (rbr) for invoice items
   */
  function recalculateRows () {
    if (!context.invoice || !context.invoice.items) return

    let rbr = context.invoice.items.length + 1
    context.invoice.items.forEach((item) => {
      rbr -= 1
      item.rbr = rbr
    })
  }

  /**
   * Recalculate invoice totals and apply discounts
   */
  function recalculate () {
    if (!context.invoice) return

    if (context.invoice.items && context.invoice.items.length > 0) {
      context.invoice.total = 0
      context.invoice.fullTotal = 0
      context.invoice.totalWithDiscount = 0
      context.invoice.totalShoppingCart = 0

      context.invoice.items.forEach((item, key) => {
        const quantityString = String(context.invoice.items[key].quantity)
        let quantity = parseFloat(quantityString.replace(',', '.'))
        if (!quantity) {
          context.invoice.items[key].quantity = 1
          quantity = 1
        }
        context.invoice.items[key].total = Math.round(context.invoice.items[key].unitPrice * quantity)
        context.invoice.items[key].fullTotal = context.invoice.items[key].total

        if (context.invoice.items[key].discountedUnitPrice !== null &&
            context.invoice.items[key].discountedUnitPrice !== undefined &&
            context.invoice.items[key].discountedUnitPrice > 0) {
          context.invoice.items[key].fullTotal = context.invoice.items[key].total
          context.invoice.items[key].total = Math.round(context.invoice.items[key].discountedUnitPrice * quantity)
        }

        context.invoice.totalWithDiscount += context.invoice.items[key].total
        context.invoice.total += context.invoice.items[key].total
        context.invoice.fullTotal += context.invoice.items[key].fullTotal
        context.invoice.totalShoppingCart += context.invoice.items[key].total
      })

      if (context.invoice.discount !== undefined) {
        if (context.invoice.discount.type === 'RATE') {
          const temp = Math.round(((context.invoice.totalShoppingCart) * (context.invoice.discount.rate / 10000)))

          context.invoice.discount.amount = temp
          context.invoice.total = Math.round(context.invoice.total - temp)
          context.invoice.totalWithDiscount = Math.round(context.invoice.total)
        } else {
          if (context.invoice.discount.amount > context.invoice.totalShoppingCart) {
            context.invoice.discount.rate = 1000
            context.invoice.discount.amount = context.invoice.totalShoppingCart
          } else {
            const temp = (context.invoice.totalShoppingCart) / (context.invoice.discount.amount)
            context.invoice.discount.rate = Math.round(temp * 100)
            context.invoice.total = Math.round(context.invoice.totalShoppingCart - context.invoice.discount.amount)
            context.invoice.totalWithDiscount = Math.round(context.invoice.totalShoppingCart - context.invoice.discount.amount)
          }
        }
      }

      // Round to 2 decimals and validate minimum amount
      if (context.invoice.total <= 125) {
        let skini = 0
        if (context.invoice.discount !== undefined) {
          skini = context.invoice.discount.amount
          if (context.$refs && context.$refs.showError) {
            context.$refs.showError.open('Uklonjen je popust na košaricu jer prelazi iznos ukupnog računa, ukoliko želite možete dodati popust na košaricu.')
          }
        }
        context.invoice.discount = undefined
        context.invoice.totalWithDiscount = 0
        context.invoice.total += skini
      }
    }

    if (!context.invoice.items || context.invoice.items.length === 0) {
      context.invoice.total = 0
      context.invoice.fullTotal = 0
      context.invoice.totalWithDiscount = 0
      context.invoice.totalShoppingCart = 0
    }
  }

  /**
   * Trigger recalculation with optional debouncing
   * @param {boolean} immediate - If true, calculate immediately; otherwise use debounced version
   */
  const debouncedRecalculate = debounce(recalculate, 150)

  function triggerRecalculate (immediate = false) {
    if (immediate) {
      // For critical operations (checkout, payment)
      recalculate()
    } else {
      // For UI updates (quantity change, discount)
      debouncedRecalculate()
    }
  }

  // Cleanup function to cancel debounced calls
  function cleanup () {
    if (debouncedRecalculate) {
      debouncedRecalculate.cancel()
    }
  }

  return {
    recalculate,
    triggerRecalculate,
    recalculateRows,
    debouncedRecalculate,
    cleanup
  }
}
